import { Type, type Static } from "@sinclair/typebox";

import {
  CatalogDetailFilterProperties,
  InvoiceCostsQuerySchema,
} from "./catalog-invoices.js";

export const CatalogRegisterQuerySchema = Type.Object(
  {
    ...CatalogDetailFilterProperties,
    managementYear: InvoiceCostsQuerySchema.properties.managementYear,
    destination: InvoiceCostsQuerySchema.properties.destination,
    category: InvoiceCostsQuerySchema.properties.category,
    queue: InvoiceCostsQuerySchema.properties.queue,
    cursor: Type.Optional(Type.String({ minLength: 1, maxLength: 512 })),
    limit: Type.Optional(
      Type.Integer({ minimum: 1, maximum: 200, default: 100 }),
    ),
  },
  { additionalProperties: false },
);

export type CatalogRegisterQuery = Static<typeof CatalogRegisterQuerySchema>;

export interface CatalogMaterial {
  id: string;
  code: string;
  description: string;
  unit: string;
  sectorId: string | null;
  sector: string;
  area: string;
  productFamily: string | null;
  purchaseCount: number;
  supplierNames: string[];
  lastPurchaseOn: string | null;
  quantityLast12: string;
  spendLast12: string;
  weightedPriceLast12: string | null;
  /** Weighted price over the selected documents; independent of quote pricing. */
  weightedPriceSelected?: string | null;
  totalQuantity: string;
  totalSpend: string;
}

export interface CatalogMaterialPage {
  items: CatalogMaterial[];
  nextCursor: string | null;
}

export interface CatalogSummary {
  materialCount: number;
  supplierCount: number;
  sectorCount: number;
  documentCount: number;
  purchaseLineCount: number;
  unassignedMaterialCount: number;
  totalSpend: string;
  spendLast12: string;
  lastImportedAt: string | null;
}

export interface CatalogSector {
  id: string;
  name: string;
  level: 1 | 2;
  parentId: string | null;
  parentName: string | null;
  materialCount: number;
  spendLast12: string;
}

export interface CatalogSupplier {
  id: string;
  name: string;
  serviceCategory: string | null;
  linkedSupplierId: string | null;
  invoiceCount: number;
  materialCount: number;
  netSpend: string;
  lastDocumentOn: string | null;
}

export const CatalogPriceSearchQuerySchema = Type.Object(
  {
    search: Type.String({ minLength: 2, maxLength: 200 }),
    limit: Type.Optional(
      Type.Integer({ minimum: 1, maximum: 50, default: 20 }),
    ),
  },
  { additionalProperties: false },
);

export const CatalogMaterialPriceParamsSchema = Type.Object(
  { id: Type.String({ format: "uuid" }) },
  { additionalProperties: false },
);

export const CatalogMaterialPriceQuerySchema = Type.Object(
  {
    limit: Type.Optional(
      Type.Integer({ minimum: 1, maximum: 50, default: 20 }),
    ),
  },
  { additionalProperties: false },
);

export const CatalogAutomationSettingsPatchSchema = Type.Object(
  {
    enabled: Type.Boolean(),
    startupCheckEnabled: Type.Boolean(),
    dayOfMonth: Type.Integer({ minimum: 1, maximum: 28 }),
  },
  { additionalProperties: false },
);

export const CatalogSyncRequestSchema = Type.Object(
  {
    operationId: Type.String({ format: "uuid" }),
    confirmed: Type.Literal(true),
  },
  { additionalProperties: false },
);

export type CatalogPriceSearchQuery = Static<
  typeof CatalogPriceSearchQuerySchema
>;
export type CatalogMaterialPriceParams = Static<
  typeof CatalogMaterialPriceParamsSchema
>;
export type CatalogMaterialPriceQuery = Static<
  typeof CatalogMaterialPriceQuerySchema
>;
export type CatalogAutomationSettingsPatch = Static<
  typeof CatalogAutomationSettingsPatchSchema
>;
export type CatalogSyncRequest = Static<typeof CatalogSyncRequestSchema>;

export interface CatalogQuoteMaterial {
  id: string;
  code: string;
  description: string;
  unit: string;
  sector: string | null;
  purchaseCount: number;
  supplierCount: number;
  lastPurchaseOn: string | null;
  latestPrice: string | null;
  weightedPriceLast12: string | null;
  quantityLast12: string;
  /** All earlier individual prices when the twelve-month average is unavailable. */
  historicalPrices: CatalogQuotePrice[];
}

export interface CatalogQuoteMaterialPage {
  items: CatalogQuoteMaterial[];
}

export interface CatalogQuotePrice {
  id: string;
  supplierName: string | null;
  documentDate: string;
  invoiceNumber: string | null;
  unit: string;
  quantity: string;
  price: string;
}

export interface CatalogMaterialPrices {
  material: {
    id: string;
    code: string;
    description: string;
    unit: string;
    weightedPriceLast12: string | null;
  };
  prices: CatalogQuotePrice[];
}

export interface CatalogAutomationSettings {
  companyId: string | null;
  enabled: boolean;
  startupCheckEnabled: boolean;
  dayOfMonth: number;
  executionState: "idle" | "running" | "failed";
  lastStartedAt: string | null;
  lastFinishedAt: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
  nextRunAt: string | null;
}

export interface CatalogSyncResult {
  batchId: string;
  replayed: boolean;
  documentCount: number;
  lineCount: number;
  materialCount: number;
  createdMaterialCount: number;
  warningCount: number;
}

export * from "./catalog-invoices.js";
